import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  LayoutDashboard,
  FileText,
  Users,
  LogOut,
  ArrowLeft,
  Send,
  Clock,
  User,
  Shield,
  Plus,
  Check,
  Menu,
  X,
  MessageSquare,
  History,
  ListTodo,
  Key,
  Paperclip,
  Download,
  Upload,
  FolderArchive,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  recebida: "bg-blue-100 text-blue-800",
  em_analise: "bg-yellow-100 text-yellow-800",
  investigacao: "bg-purple-100 text-purple-800",
  concluida: "bg-green-100 text-green-800",
  arquivada: "bg-slate-100 text-slate-800",
  respondida: "bg-green-100 text-green-800",
  fechada: "bg-slate-100 text-slate-800",
};

// Map Portuguese DB values to English translation keys
const ombudsmanTypeMap = {
  sugestao: "suggestion",
  reclamacao: "complaint",
  elogio: "compliment",
  informacao: "information",
};

const categoryMap = {
  fraude: "fraud",
  assedio: "harassment",
  seguranca: "safety",
  corrupcao: "corruption",
  outro: "other",
};

export default function CaseDetails({ type, multiTenant = false }) {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, empresa, logout, loading: authLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investigators, setInvestigators] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newDespacho, setNewDespacho] = useState("");
  const [newTarefa, setNewTarefa] = useState({ titulo: "", descricao: "", prazo: "" });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tarefaDialogOpen, setTarefaDialogOpen] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [anexoToDelete, setAnexoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isDenuncia = type === "denuncia";
  const endpoint = isDenuncia ? "denuncias" : "ouvidoria";
  
  // Multi-tenant API base
  const apiBase = multiTenant ? `${API_URL}/empresas/${slug}` : API_URL;
  const adminBase = multiTenant ? `/${slug}/admin` : "/admin";

  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (authLoading) return;
    
    if (!user) {
      if (multiTenant) {
        navigate(`/${slug}/login`);
      } else {
        navigate("/login");
      }
      return;
    }
    fetchData();
    // Fetch investigators for multi-tenant (both denuncias and ouvidoria)
    if (multiTenant) {
      fetchInvestigators();
    }
    fetchAnexos();
  }, [id, type, slug, user, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/${endpoint}/${id}`);
      setCaseData(response.data);
    } catch (error) {
      console.error("Error fetching case:", error);
      if (error.response?.status === 403) {
        toast.error("Você não tem acesso a este caso");
        navigate(`${adminBase}/casos`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestigators = async () => {
    try {
      // For multi-tenant, fetch from empresa investigadores endpoint
      const response = await axios.get(`${API_URL}/empresas/${slug}/investigadores`);
      // Filter only active investigators
      setInvestigators(response.data.filter(inv => inv.ativo));
    } catch (error) {
      console.error("Error fetching investigators:", error);
    }
  };

  const fetchAnexos = async () => {
    try {
      const response = await axios.get(`${apiBase}/${endpoint}/${id}/anexos`);
      setAnexos(response.data);
    } catch (error) {
      console.error("Error fetching anexos:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${apiBase}/${endpoint}/${id}/anexo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Arquivo anexado com sucesso!");
      fetchAnexos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao anexar arquivo");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadAnexo = async (anexoId, filename) => {
    try {
      toast.info("Preparando download...");
      const response = await axios.get(
        `${API_URL}/empresas/${slug}/anexos/${anexoId}`,
        { responseType: "blob" }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "anexo");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading anexo:", error);
      toast.error(error.response?.data?.detail || "Erro ao baixar anexo");
    }
  };

  const openDeleteModal = (anexo) => {
    setAnexoToDelete(anexo);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setAnexoToDelete(null);
  };

  const confirmDeleteAnexo = async () => {
    if (!anexoToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/empresas/${slug}/anexos/${anexoToDelete.id}`);
      
      // Remove the deleted anexo from the list immediately
      setAnexos(prevAnexos => prevAnexos.filter(a => a.id !== anexoToDelete.id));
      
      toast.success("Anexo excluído com sucesso");
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting anexo:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir anexo");
    } finally {
      setDeleting(false);
    }
  };

  const downloadAnexosZip = async () => {
    try {
      toast.info("Preparando download dos anexos...");
      const tipoParam = isDenuncia ? "denuncia" : "ouvidoria";
      const response = await axios.get(
        `${apiBase}/casos/${id}/anexos-zip?tipo=${tipoParam}`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `anexos_${data?.protocolo || id}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download concluído!");
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      if (error.response?.status === 404) {
        toast.error("Nenhum anexo encontrado para download");
      } else {
        toast.error("Erro ao baixar anexos");
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`${apiBase}/${endpoint}/${id}/status`, { status: newStatus });
      toast.success("Status atualizado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const updatePrioridade = async (newPrioridade) => {
    if (!isDenuncia) return;
    try {
      await axios.put(`${apiBase}/denuncias/${id}/prioridade`, { prioridade: newPrioridade });
      toast.success("Prioridade atualizada!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar prioridade");
    }
  };

  const assignCase = async (responsavelId) => {
    try {
      await axios.post(`${apiBase}/${endpoint}/${id}/atribuir`, { responsavel_id: responsavelId });
      toast.success("Investigador atribuído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atribuir investigador");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      if (isDenuncia) {
        await axios.post(`${apiBase}/denuncias/${id}/mensagens`, { conteudo: newMessage });
        toast.success("Mensagem enviada!");
      } else {
        // Ouvidoria - anotações internas
        await axios.post(`${apiBase}/ouvidoria/${id}/respostas`, { conteudo: newMessage });
        toast.success("Anotação registrada!");
      }
      setNewMessage("");
      fetchData();
    } catch (error) {
      toast.error(isDenuncia ? "Erro ao enviar mensagem" : "Erro ao registrar anotação");
    } finally {
      setSendingMessage(false);
    }
  };

  const createDespacho = async (e) => {
    e.preventDefault();
    if (!newDespacho.trim()) return;
    try {
      await axios.post(`${apiBase}/denuncias/${id}/despacho?publico=false`, {
        conteudo: newDespacho,
      });
      toast.success("Despacho registrado!");
      setNewDespacho("");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar despacho");
    }
  };

  const createTarefa = async (e) => {
    e.preventDefault();
    if (!newTarefa.titulo.trim()) return;
    try {
      await axios.post(`${apiBase}/denuncias/${id}/tarefa`, newTarefa);
      toast.success("Tarefa criada!");
      setNewTarefa({ titulo: "", descricao: "", prazo: "" });
      setTarefaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar tarefa");
    }
  };

  const completeTarefa = async (tarefaId) => {
    try {
      await axios.put(`${apiBase}/tarefas/${tarefaId}/concluir`);
      toast.success("Tarefa concluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao concluir tarefa");
    }
  };

  const formatDate = (dateStr) => {
    const locale = t("common.locale") === "en" ? "en-US" : "pt-BR";
    return new Date(dateStr).toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canEdit = ["admin", "gestor", "investigador"].includes(user?.role);

  const handleLogout = () => {
    logout();
    if (multiTenant && slug) {
      navigate(`/${slug}/login`);
    } else {
      navigate("/login");
    }
  };

  // Get empresa name for sidebar
  const empresaNome = multiTenant ? (empresa?.nome || slug?.toUpperCase()) : "CONTERP";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <Link to={adminBase} className="flex items-center gap-3">
                {multiTenant && empresa?.logo_url ? (
                  <img src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} alt="" className="h-10 w-auto" />
                ) : (
                  <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">{empresaNome}</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </Link>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link to={adminBase} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5" />
              {t("dashboard.menu.dashboard")}
            </Link>
            <Link to={`${adminBase}/casos`} className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium">
              <FileText className="w-5 h-5" />
              {t("dashboard.menu.cases")}
            </Link>
            {multiTenant && user?.role === "admin" && (
              <Link to={`${adminBase}/investigadores`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
                <Users className="w-5 h-5" />
                {t("dashboard.menu.investigators")}
              </Link>
            )}
            {!multiTenant && user?.role === "admin" && (
              <Link to={`${adminBase}/usuarios`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
                <Users className="w-5 h-5" />
                {t("dashboard.menu.users")}
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout} data-testid="btn-logout">
              <LogOut className="w-4 h-4 mr-2" />
              {t("dashboard.menu.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <Link to={`${adminBase}/casos`} className="text-slate-600 hover:text-[#1a1a2e]">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-[#1a1a2e]">{caseData?.protocolo}</h1>
                <p className="text-sm text-slate-500">
                  {isDenuncia ? t("cases.whistleblowing") : t("cases.ombudsman")} • {t("caseDetails.createdAt")} {formatDate(caseData?.created_at)}
                </p>
              </div>
            </div>
            <Badge className={statusColors[caseData?.status]} data-testid="case-status">
              {t(`caseDetails.statusLabels.${caseData?.status === "recebida" ? "received" : caseData?.status === "em_analise" ? "in_progress" : caseData?.status === "investigacao" ? "investigation" : caseData?.status === "concluida" ? "concluded" : caseData?.status === "arquivada" ? "archived" : caseData?.status === "respondida" ? "answered" : "closed"}`)}
            </Badge>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-[#1a1a2e] mb-4">{t("caseDetails.description")}</h2>
                <p className="text-slate-600 whitespace-pre-wrap">{caseData?.descricao}</p>
                
                {isDenuncia && (
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-200">
                    {caseData?.data_fato && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.dateOfIncident")}</span>
                        <p className="text-[#1a1a2e]">{caseData.data_fato}</p>
                      </div>
                    )}
                    {caseData?.local && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.location")}</span>
                        <p className="text-[#1a1a2e]">{caseData.local}</p>
                      </div>
                    )}
                    {caseData?.pessoas_envolvidas && (
                      <div className="col-span-2">
                        <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.peopleInvolved")}</span>
                        <p className="text-[#1a1a2e]">{caseData.pessoas_envolvidas}</p>
                      </div>
                    )}
                  </div>
                )}

                {!isDenuncia && (
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-200">
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.name")}</span>
                      <p className="text-[#1a1a2e]">{caseData?.nome}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.email")}</span>
                      <p className="text-[#1a1a2e]">{caseData?.email}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.phone")}</span>
                      <p className="text-[#1a1a2e]">{caseData?.telefone}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">{t("caseDetails.slaDeadline")}</span>
                      <p className="text-[#1a1a2e]">{formatDate(caseData?.sla_prazo)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tabs for Messages, Dispatches, Tasks, History */}
              <Tabs defaultValue="messages" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-slate-50 p-0">
                  <TabsTrigger value="messages" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1a1a2e] data-[state=active]:bg-white">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {isDenuncia ? t("caseDetails.tabs.anonymousChat") : t("caseDetails.tabs.notes", "Anotações")}
                  </TabsTrigger>
                  {isDenuncia && (
                    <>
                      <TabsTrigger value="despachos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1a1a2e] data-[state=active]:bg-white">
                        <FileText className="w-4 h-4 mr-2" />
                        {t("caseDetails.tabs.dispatches")}
                      </TabsTrigger>
                      <TabsTrigger value="tarefas" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1a1a2e] data-[state=active]:bg-white">
                        <ListTodo className="w-4 h-4 mr-2" />
                        {t("caseDetails.tabs.tasks")}
                      </TabsTrigger>
                    </>
                  )}
                  <TabsTrigger value="anexos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1a1a2e] data-[state=active]:bg-white">
                    <Paperclip className="w-4 h-4 mr-2" />
                    {t("caseDetails.attachments")} ({anexos.length})
                  </TabsTrigger>
                  <TabsTrigger value="historico" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1a1a2e] data-[state=active]:bg-white">
                    <History className="w-4 h-4 mr-2" />
                    {t("caseDetails.history")}
                  </TabsTrigger>
                </TabsList>

                {/* Messages/Notes Tab - For both Denuncias and Ouvidoria */}
                <TabsContent value="messages" className="p-0 m-0">
                  <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
                    {isDenuncia ? (
                      // Denúncia - mensagens
                      <>
                        {caseData?.mensagens?.length === 0 && (
                          <p className="text-center text-slate-500 py-8">{t("caseDetails.noMessages")}</p>
                        )}
                        {caseData?.mensagens?.map((msg, i) => (
                          <div
                            key={i}
                            className={`flex ${
                              msg.remetente === "admin" || msg.remetente === "investigador"
                                ? "justify-end"
                                : "justify-start"
                            }`}
                          >
                            <div
                              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                                msg.remetente === "admin" || msg.remetente === "investigador"
                                  ? "bg-[#1a1a2e] text-white rounded-br-sm"
                                  : "bg-slate-100 text-[#1a1a2e] rounded-bl-sm"
                              }`}
                            >
                              <p className="text-sm">{msg.conteudo}</p>
                              <p className={`text-xs mt-1 ${
                                msg.remetente === "admin" || msg.remetente === "investigador"
                                  ? "text-white/60"
                                  : "text-slate-500"
                              }`}>
                                {msg.remetente === "admin" || msg.remetente === "investigador"
                                  ? msg.remetente_nome || t("caseDetails.investigator")
                                  : t("caseDetails.anonymousReporter")}{" "}
                                • {formatDate(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      // Ouvidoria - anotações internas (respostas são por email)
                      <>
                        {(!caseData?.respostas || caseData?.respostas?.length === 0) && (
                          <p className="text-center text-slate-500 py-8">
                            {t("caseDetails.noNotes", "Nenhuma anotação registrada")}
                          </p>
                        )}
                        {caseData?.respostas?.map((resp, i) => (
                          <div key={i} className="flex justify-end">
                            <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-[#1a1a2e] text-white rounded-br-sm">
                              <p className="text-sm">{resp.conteudo}</p>
                              <p className="text-xs mt-1 text-white/60">
                                {resp.autor_nome || t("caseDetails.investigator")} • {formatDate(resp.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  {canEdit && (
                    <form onSubmit={sendMessage} className="p-4 border-t border-slate-200">
                      <div className="flex gap-3">
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={isDenuncia ? t("caseDetails.messagePlaceholder") : t("caseDetails.notePlaceholder", "Digite sua anotação...")}
                          className="flex-1 min-h-[60px]"
                          data-testid="message-input"
                        />
                        <Button
                          type="submit"
                          disabled={sendingMessage || !newMessage.trim()}
                          className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90 self-end"
                          data-testid="btn-send-message"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </form>
                  )}
                </TabsContent>

                {/* Despachos Tab */}
                {isDenuncia && (
                  <TabsContent value="despachos" className="p-4 m-0">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium text-[#1a1a2e]">{t("caseDetails.tabs.dispatches")}</h3>
                      {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-[#1a1a2e]" data-testid="btn-new-despacho">
                              <Plus className="w-4 h-4 mr-1" />
                              {t("caseDetails.newDispatch")}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{t("caseDetails.newDispatch")}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={createDespacho} className="space-y-4">
                              <div>
                                <Label>{t("caseDetails.content")}</Label>
                                <Textarea
                                  value={newDespacho}
                                  onChange={(e) => setNewDespacho(e.target.value)}
                                  placeholder={t("caseDetails.dispatchPlaceholder")}
                                  className="mt-2"
                                />
                              </div>
                              <Button type="submit" className="w-full bg-[#1a1a2e]">
                                {t("caseDetails.registerDispatch")}
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                    <div className="space-y-4 max-h-[300px] overflow-y-auto">
                      {caseData?.despachos?.length === 0 && (
                        <p className="text-center text-slate-500 py-8">{t("caseDetails.noDispatches")}</p>
                      )}
                      {caseData?.despachos?.map((d, i) => (
                        <div key={i} className="border-l-2 border-[#f5a623] pl-4 py-2">
                          <p className="text-sm text-[#1a1a2e]">{d.conteudo}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <span>{d.user_nome}</span>
                            <span>•</span>
                            <span>{formatDate(d.created_at)}</span>
                            {d.publico && <Badge variant="outline" className="text-xs">{t("caseDetails.public")}</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                )}

                {/* Tarefas Tab */}
                {isDenuncia && (
                  <TabsContent value="tarefas" className="p-4 m-0">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium text-[#1a1a2e]">{t("caseDetails.tabs.tasks")}</h3>
                      {canEdit && (
                        <Dialog open={tarefaDialogOpen} onOpenChange={setTarefaDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-[#1a1a2e]" data-testid="btn-new-tarefa">
                              <Plus className="w-4 h-4 mr-1" />
                              {t("caseDetails.newTask")}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{t("caseDetails.newTask")}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={createTarefa} className="space-y-4">
                              <div>
                                <Label>{t("caseDetails.taskTitle")}</Label>
                                <Input
                                  value={newTarefa.titulo}
                                  onChange={(e) => setNewTarefa({ ...newTarefa, titulo: e.target.value })}
                                  className="mt-2"
                                />
                              </div>
                              <div>
                                <Label>{t("caseDetails.taskDescription")}</Label>
                                <Textarea
                                  value={newTarefa.descricao}
                                  onChange={(e) => setNewTarefa({ ...newTarefa, descricao: e.target.value })}
                                  className="mt-2"
                                />
                              </div>
                              <div>
                                <Label>{t("caseDetails.taskDeadline")}</Label>
                                <Input
                                  type="date"
                                  value={newTarefa.prazo}
                                  onChange={(e) => setNewTarefa({ ...newTarefa, prazo: e.target.value })}
                                  className="mt-2"
                                />
                              </div>
                              <Button type="submit" className="w-full bg-[#1a1a2e]">
                                {t("caseDetails.createTask")}
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {caseData?.tarefas?.length === 0 && (
                        <p className="text-center text-slate-500 py-8">{t("caseDetails.noTasks")}</p>
                      )}
                      {caseData?.tarefas?.map((t2, i) => (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            t2.concluida ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                          }`}
                        >
                          {canEdit && !t2.concluida && (
                            <button
                              onClick={() => completeTarefa(t2.id)}
                              className="w-6 h-6 rounded-full border-2 border-slate-300 hover:border-green-500 hover:bg-green-50 flex items-center justify-center"
                            >
                              <Check className="w-3 h-3 text-transparent hover:text-green-500" />
                            </button>
                          )}
                          {t2.concluida && (
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className={`font-medium ${t2.concluida ? "line-through text-slate-400" : "text-[#1a1a2e]"}`}>
                              {t2.titulo}
                            </p>
                            {t2.descricao && <p className="text-sm text-slate-500">{t2.descricao}</p>}
                          </div>
                          {t2.prazo && (
                            <Badge variant="outline" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {t2.prazo}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                )}

                {/* Histórico Tab */}
                <TabsContent value="historico" className="p-4 m-0">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {caseData?.historico?.length === 0 && (
                      <p className="text-center text-slate-500 py-8">{t("caseDetails.noHistory")}</p>
                    )}
                    {caseData?.historico?.map((h, i) => (
                      <div key={i} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-[#1a1a2e] mt-2"></div>
                        <div>
                          <p className="text-[#1a1a2e]">
                            <span className="font-medium">{h.acao}</span>
                            {h.detalhes && ` - ${h.detalhes}`}
                          </p>
                          <p className="text-slate-500 text-xs">{formatDate(h.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Anexos Tab */}
                <TabsContent value="anexos" className="p-4 m-0">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-[#1a1a2e]">{t("caseDetails.attachments")}</h3>
                    <div className="flex gap-2">
                      {anexos.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={downloadAnexosZip}
                          data-testid="btn-download-all-zip"
                        >
                          <FolderArchive className="w-4 h-4 mr-1" />
                          Baixar Todos (ZIP)
                        </Button>
                      )}
                      {canEdit && (
                        <div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.mp3,.mp4,.wav"
                          />
                          <Button
                            size="sm"
                            className="bg-[#1a1a2e]"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingFile}
                            data-testid="btn-upload-anexo"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {uploadingFile ? t("caseDetails.uploading") : t("caseDetails.addAttachment")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {anexos.length === 0 && (
                      <p className="text-center text-slate-500 py-8">{t("caseDetails.noAttachments")}</p>
                    )}
                    {anexos.map((anexo) => (
                      <div
                        key={anexo.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="w-10 h-10 bg-[#1a1a2e]/10 rounded-lg flex items-center justify-center">
                          <Paperclip className="w-5 h-5 text-[#1a1a2e]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1a1a2e] truncate">{anexo.filename}</p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(anexo.file_size)} • {anexo.uploaded_by} • {formatDate(anexo.created_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadAnexo(anexo.id, anexo.filename)}
                            data-testid={`download-${anexo.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteModal(anexo)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${anexo.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar Actions */}
            <div className="space-y-6">
              {/* Status & Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("caseDetails.actions")}</h3>
                
                {canEdit && (
                  <div className="space-y-4">
                    {/* Status */}
                    <div>
                      <Label className="text-sm text-slate-500">{t("caseDetails.status")}</Label>
                      <Select value={caseData?.status} onValueChange={updateStatus}>
                        <SelectTrigger className="mt-2" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {isDenuncia ? (
                            <>
                              <SelectItem value="recebida">{t("caseDetails.statusLabels.received")}</SelectItem>
                              <SelectItem value="em_analise">{t("caseDetails.statusLabels.in_progress")}</SelectItem>
                              <SelectItem value="investigacao">{t("caseDetails.statusLabels.investigation")}</SelectItem>
                              <SelectItem value="concluida">{t("caseDetails.statusLabels.concluded")}</SelectItem>
                              <SelectItem value="arquivada">{t("caseDetails.statusLabels.archived")}</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="recebida">{t("caseDetails.statusLabels.received")}</SelectItem>
                              <SelectItem value="em_analise">{t("caseDetails.statusLabels.in_progress")}</SelectItem>
                              <SelectItem value="respondida">{t("caseDetails.statusLabels.answered")}</SelectItem>
                              <SelectItem value="fechada">{t("caseDetails.statusLabels.closed")}</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Priority (only for denúncias) */}
                    {isDenuncia && (
                      <div>
                        <Label className="text-sm text-slate-500">{t("caseDetails.priority")}</Label>
                        <Select value={caseData?.prioridade} onValueChange={updatePrioridade}>
                          <SelectTrigger className="mt-2" data-testid="prioridade-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="baixa">{t("caseDetails.priorityLabels.low")}</SelectItem>
                            <SelectItem value="media">{t("caseDetails.priorityLabels.medium")}</SelectItem>
                            <SelectItem value="alta">{t("caseDetails.priorityLabels.high")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Assign - For both denuncias and ouvidoria in multi-tenant */}
                    {multiTenant && (
                      <div>
                        <Label className="text-sm text-slate-500">{t("caseDetails.investigator")}</Label>
                        <Select value={caseData?.responsavel_id || ""} onValueChange={assignCase}>
                          <SelectTrigger className="mt-2" data-testid="responsavel-select">
                            <SelectValue placeholder={t("caseDetails.selectInvestigator")} />
                          </SelectTrigger>
                          <SelectContent>
                            {investigators.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-slate-500">
                                {t("caseDetails.noInvestigators")}
                              </div>
                            ) : (
                              investigators.map((inv) => (
                                <SelectItem key={inv.id} value={inv.id}>
                                  {inv.nome} {inv.cargo ? `(${inv.cargo})` : ""}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        {caseData?.responsavel_nome && (
                          <div className="mt-2 p-2 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-600">
                              <span className="font-medium">{t("caseDetails.current")}:</span> {caseData.responsavel_nome}
                            </p>
                            {caseData?.responsavel_email && (
                              <p className="text-xs text-slate-500">{caseData.responsavel_email}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Case Info */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("caseDetails.info")}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t("caseDetails.type")}</span>
                    <span className="text-[#1a1a2e] font-medium">
                      {isDenuncia ? t("cases.whistleblowing") : t("cases.ombudsman")}
                    </span>
                  </div>
                  {isDenuncia && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t("caseDetails.category")}</span>
                      <Badge variant="outline" className="capitalize">{t(`caseDetails.categoryLabels.${categoryMap[caseData?.categoria] || caseData?.categoria}`)}</Badge>
                    </div>
                  )}
                  {!isDenuncia && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t("caseDetails.type")}</span>
                      <Badge variant="outline" className="capitalize">{t(`caseDetails.ombudsmanTypes.${ombudsmanTypeMap[caseData?.tipo] || caseData?.tipo}`)}</Badge>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t("caseDetails.registered")}</span>
                    <span className="text-[#1a1a2e]">{formatDate(caseData?.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Credentials for Admin/Gestor - Denuncia only */}
              {isDenuncia && caseData?.senha_admin && (user?.role === "admin" || user?.role === "gestor") && (
                <div className="bg-[#f5a623]/10 rounded-xl border border-[#f5a623]/30 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-[#f5a623]" />
                    <h3 className="font-semibold text-[#1a1a2e]">{t("caseDetails.accessCredentials")}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    {t("caseDetails.credentialsHint")}
                  </p>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-slate-500 block mb-1">{t("caseDetails.protocol")}</span>
                      <code className="bg-white px-3 py-2 rounded-lg font-mono text-[#1a1a2e] block">
                        {caseData?.protocolo}
                      </code>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">{t("caseDetails.password")}</span>
                      <code className="bg-white px-3 py-2 rounded-lg font-mono text-[#1a1a2e] block font-bold">
                        {caseData?.senha_admin}
                      </code>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && anexoToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Confirmar Exclusão</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-600 mb-3">
                Você está prestes a excluir o seguinte arquivo:
              </p>
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-slate-400" />
                <span className="font-medium text-slate-800 truncate">{anexoToDelete.filename}</span>
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>Atenção:</strong> Esta ação não pode ser desfeita. O arquivo será permanentemente removido do sistema.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDeleteAnexo}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Excluindo..." : "Excluir Arquivo"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
